import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { map, Observable, filter, Subject, takeUntil } from 'rxjs';

import {
  FeatureNames,
  ActivateDeactivateCommands,
  SetNumberValueCommands,
  FeatureCommands,
  Feature,
  IsActiveProperty,
  IsNumberValueProperty,
  FeaturePropertyKey,
  TariffBasedFinancialOptimizationStateProperty,
  TariffBasedFinancialOptimizationStateCommands,
  HemsConfigurationOverviewProperties,
  HemsType,
  EmsHocaFeaturesProperties,
  DeviceIds,
  NumberInWords,
  DryContactStatus,
  PhotovoltaicPriorityListCommands,
  PhotovoltaicPriorityListProperties,
} from '@vi-iot/client';

import { DevicesStore, HemsStore, InstallationFeaturesStore } from '@store';
import { UIService } from '@services';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DevicesService } from 'src/app/@store/devices/devices.service';

@Component({
  selector: 'app-hems-settings',
  templateUrl: './hems-settings.component.html',
  styleUrls: ['./hems-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HemsSettingsComponent implements OnInit, OnDestroy {
  photovoltaicExcess$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  photovoltaicPvExcess$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  solarIrradiance$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  forecastBased$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  overloadProtection$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  gridCurrentLimit$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  featureTariffBasedFinancialOptimizationState$: Observable<
    Feature<
      TariffBasedFinancialOptimizationStateProperty,
      TariffBasedFinancialOptimizationStateCommands
    >
  >;
  featureAllowBatteryChargingFromTheGrid$: Observable<
    Feature<IsActiveProperty, ActivateDeactivateCommands>
  >;
  hocaFeatures$: Observable<Feature<EmsHocaFeaturesProperties, never>>;
  hemsActiveSubscription$: Observable<HemsType>;
  dryContactOptimizationToggles$: Observable<DryContactStatus[]>;
  private ngUnsubscribe$ = new Subject<void>();
  showPVPrioSection: boolean;

  featureHeatpump$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  readonly featureCommands = FeatureCommands;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly hemsType = HemsType;

  wallboxDevices$: Observable<{ selectId: string; name: string }[]>;

  constructor(
    public devicesStore: DevicesStore,
    public hemsStore: HemsStore,
    public devicesService: DevicesService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private uiService: UIService
  ) {}

  ngOnInit(): void {
    this.photovoltaicExcess$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.photovoltaicExcessHeatpumpHandling
    );
    this.photovoltaicPvExcess$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.photovoltaicExcessPvHandling
    );
    this.solarIrradiance$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.onlineSolarIrradianceForecast
    );
    this.forecastBased$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.forecastBasedBatteryOperation
    );
    this.overloadProtection$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.overloadProtection
    );
    this.gridCurrentLimit$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.gridCurrentLimit
    );
    this.featureTariffBasedFinancialOptimizationState$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.emsHocaFeaturesTariffBasedFinancialOptimizationState
    );
    this.featureAllowBatteryChargingFromTheGrid$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.emsHocaFeaturesAllowBatteryChargingFromTheGrid
    );
    this.featureHeatpump$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.emsHocaFeaturesHeatpumpOnly
    );
    this.hemsActiveSubscription$ = this.installationFeaturesStore
      .selectInstallationGatewayFeature(FeatureNames.hemsConfigurationOverview)
      .pipe(
        filter((feature) => !!feature),
        map(
          (feature: Feature<HemsConfigurationOverviewProperties, never>) =>
            feature?.properties?.activeHemsType?.value
        )
      );
    this.dryContactOptimizationToggles$ = this.installationFeaturesStore
      .selectInstallationHemsFeature(FeatureNames.hocaFeatures)
      .pipe(
        map((feature: Feature<EmsHocaFeaturesProperties, never>) => {
          const { supported } = feature.properties;

          return supported.value
            .filter((featureName) => this.isPVExcessDryContactFeature(featureName))
            .map((featureName) => {
              const busId = this.busNumberFromFeature(featureName);

              return {
                busId,
                featureName,
                deviceName$: this.installationFeaturesStore.selectInstallationDeviceFeature(
                  FeatureNames.deviceName,
                  this.getHemsDeviceId(busId)
                ),
                deviceOptimisationToggleFeature$: this.installationFeaturesStore.selectInstallationDeviceFeature(
                  FeatureNames.emsEnergyPVExcessToDryContact(featureName),
                  DeviceIds.Hems
                ),
              } as DryContactStatus;
            });
        })
      );

    this.installationFeaturesStore
      .selectInstallationHemsFeature(FeatureNames.pvExcessPowerPrioritization)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (
          feature: Feature<PhotovoltaicPriorityListProperties, PhotovoltaicPriorityListCommands>
        ) => {
          this.showPVPrioSection = !!feature && feature?.properties?.devices?.value?.length >= 2;
        }
      );

    this.hocaFeatures$ = this.installationFeaturesStore.selectInstallationHemsFeature<
      EmsHocaFeaturesProperties,
      never
    >(FeatureNames.hocaFeatures);
  }

  updateDryContactOptimisation(
    value: MatSlideToggleChange,
    feature: Feature<IsActiveProperty, never>
  ): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      value.checked,
      FeaturePropertyKey.active,
      FeaturePropertyKey.active,
      FeatureCommands.setActive,
      feature.gatewayId,
      feature.deviceId
    );
  }

  private isPVExcessDryContactFeature(feature: string): boolean {
    return /^photovoltaicExcessToDryContactControlledDevice\w+$/.test(feature);
  }

  private busNumberFromFeature(feature: string): number {
    // example feature: photovoltaicExcessToDryContactControlledDeviceOne return 2
    switch (this.extractDryContactDeviceNumber(feature)) {
      case NumberInWords.One:
        return 2;
      case NumberInWords.Two:
        return 3;
      case NumberInWords.Three:
        return 4;
      case NumberInWords.Four:
        return 5;
      case NumberInWords.Five:
        return 6;
    }
  }

  private getHemsDeviceId(id: number): string {
    return `hems-${id}`;
  }

  private extractDryContactDeviceNumber(featureName: string): string | null {
    return featureName.match(/^photovoltaicExcessToDryContactControlledDevice(\w+)$/)?.[1] ?? null;
  }

  onOpenChargingMode(wallBoxDeviceSelectId): void {
    this.devicesStore.setSelectedDevice(wallBoxDeviceSelectId);
    this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.WallboxChargingSettings);
  }

  onOpenPvPowerPrioritization(): void {
    this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.PvPowerPrioritization);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
